// src/pages/Admin/OrdersManager.jsx
import React, { useState, useEffect } from 'react';
import { collectionGroup, getDocs, updateDoc } from 'firebase/firestore';
import { db } from '../services/firebase';
import '../styles/Admin.css';

const OrdersManager = ({ setMessage }) => {
    const [orders, setOrders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState('all');
    const [selectedOrder, setSelectedOrder] = useState(null);

    // --- NEW: Shipping Modal State ---
    const [showShippingModal, setShowShippingModal] = useState(false);
    const [orderToShip, setOrderToShip] = useState(null);
    const [shippingDetails, setShippingDetails] = useState({ trackingNumber: '', courier: 'Royal Mail' });
    const [isSendingEmail, setIsSendingEmail] = useState(false);

    const fetchOrders = async () => {
        setLoading(true);
        try {
            const ordersQuery = collectionGroup(db, 'orders');
            const snapshot = await getDocs(ordersQuery);

            let ordersData = snapshot.docs.map(doc => ({
                id: doc.id,
                ref: doc.ref,
                ...doc.data()
            }));

            ordersData.sort((a, b) => {
                const dateA = a.createdAt?.toDate() || new Date(0);
                const dateB = b.createdAt?.toDate() || new Date(0);
                return dateB - dateA;
            });

            setOrders(ordersData);
        } catch (err) {
            console.error('Error fetching orders:', err);
            setMessage('Failed to load orders. Please try again later.');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchOrders();
    }, []);

    // Standard status update for Cancelling or Delivering
    const updateOrderStatus = async (orderRef, newStatus) => {
        try {
            await updateDoc(orderRef, {
                status: newStatus,
                updatedAt: new Date()
            });
            setMessage(`Order status updated to ${newStatus}`);
            fetchOrders();
        } catch (err) {
            console.error('Error updating order:', err);
            setMessage('Failed to update order status.');
        }
    };

    // --- NEW: Handle the complete Shipping + Email workflow ---
    const handleDispatchOrder = async (e) => {
        e.preventDefault();
        setIsSendingEmail(true);

        // Smart routing for the email API
        const API_BASE_URL = window.location.hostname === 'localhost'
            ? 'http://localhost:5001'
            : 'https://api.bundlemadnesshair.com';

        try {
            // 1. Send the email via our backend endpoint
            const emailResponse = await fetch(`${API_BASE_URL}/api/send-shipping-email`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    customerEmail: orderToShip.customerEmail,
                    orderId: orderToShip.id,
                    trackingNumber: shippingDetails.trackingNumber,
                    courier: shippingDetails.courier
                })
            });

            if (!emailResponse.ok) {
                throw new Error("Failed to send shipping email");
            }

            // 2. Update Firestore with new status and tracking info
            await updateDoc(orderToShip.ref, {
                status: 'shipped',
                trackingNumber: shippingDetails.trackingNumber,
                courier: shippingDetails.courier,
                shippedAt: new Date(),
                updatedAt: new Date()
            });

            setMessage(`Order successfully marked as shipped. Email sent to customer!`);
            setShowShippingModal(false);
            setOrderToShip(null);
            setShippingDetails({ trackingNumber: '', courier: 'Royal Mail' }); // reset
            fetchOrders(); // refresh list

        } catch (error) {
            console.error("Fulfillment error:", error);
            setMessage('Error dispatching order. Please try again.');
        } finally {
            setIsSendingEmail(false);
        }
    };

    const getStatusCounts = () => {
        return {
            all: orders.length,
            paid: orders.filter(o => o.status === 'paid').length,
            shipped: orders.filter(o => o.status === 'shipped').length,
            delivered: orders.filter(o => o.status === 'delivered').length,
        };
    };

    const statusCounts = getStatusCounts();
    const displayedOrders = filter === 'all' ? orders : orders.filter(o => o.status === filter);

    // Orders store Stripe's currency code ('gbp' / 'usd'); older orders have none and are GBP
    const formatPrice = (price, currency = 'gbp') => {
        const numPrice = Number(price);
        const code = (currency || 'gbp').toUpperCase() === 'USD' ? 'USD' : 'GBP';
        return new Intl.NumberFormat(code === 'USD' ? 'en-US' : 'en-GB', { style: 'currency', currency: code })
            .format(!isNaN(numPrice) ? numPrice : 0);
    };

    const formatOrderId = (id) => {
        if (!id) return "UNKNOWN";
        if (id.startsWith("cs_")) return id.split("_").pop().slice(-8).toUpperCase();
        return id.slice(0, 8).toUpperCase();
    };

    if (loading) {
        return (
            <div className="loading-state">
                <div className="loading-spinner"></div>
                <p>Loading orders...</p>
            </div>
        );
    }

    return (
        <div className="orders-manager" style={{ position: 'relative' }}>
            {/* --- SHIPPING MODAL OVERLAY --- */}
            {showShippingModal && (
                <div style={{
                    position: 'fixed', top: 0, left: 0, width: '100%', height: '100%',
                    backgroundColor: 'rgba(0,0,0,0.8)', zIndex: 9999,
                    display: 'flex', justifyContent: 'center', alignItems: 'center'
                }}>
                    <div style={{
                        background: '#111', border: '1px solid #d4af37', padding: '30px',
                        width: '100%', maxWidth: '500px', borderRadius: '4px'
                    }}>
                        <h3 style={{ color: '#d4af37', fontFamily: "'Cinzel', serif", marginBottom: '20px' }}>
                            Dispatch Order #{formatOrderId(orderToShip?.id)}
                        </h3>
                        <p style={{ color: '#a0aec0', fontSize: '0.9rem', marginBottom: '25px' }}>
                            Entering this information will immediately send a shipping confirmation email to <strong>{orderToShip?.customerEmail}</strong>.
                        </p>

                        <form onSubmit={handleDispatchOrder}>
                            <div style={{ marginBottom: '15px' }}>
                                <label style={{ display: 'block', color: '#fff', fontSize: '0.8rem', textTransform: 'uppercase', marginBottom: '8px' }}>Courier</label>
                                <select
                                    value={shippingDetails.courier}
                                    onChange={(e) => setShippingDetails({...shippingDetails, courier: e.target.value})}
                                    style={{ width: '100%', padding: '12px', background: '#000', border: '1px solid #333', color: '#fff' }}
                                >
                                    <option value="Royal Mail">Royal Mail</option>
                                    <option value="DPD">DPD</option>
                                    <option value="DHL">DHL</option>
                                    <option value="FedEx">FedEx</option>
                                    <option value="UPS">UPS</option>
                                </select>
                            </div>

                            <div style={{ marginBottom: '25px' }}>
                                <label style={{ display: 'block', color: '#fff', fontSize: '0.8rem', textTransform: 'uppercase', marginBottom: '8px' }}>Tracking Number</label>
                                <input
                                    type="text"
                                    placeholder="e.g. JY123456789GB"
                                    value={shippingDetails.trackingNumber}
                                    onChange={(e) => setShippingDetails({...shippingDetails, trackingNumber: e.target.value})}
                                    style={{ width: '100%', padding: '12px', background: '#000', border: '1px solid #333', color: '#fff' }}
                                    required
                                />
                            </div>

                            <div style={{ display: 'flex', gap: '15px' }}>
                                <button
                                    type="submit"
                                    disabled={isSendingEmail}
                                    style={{ flex: 1, padding: '12px', background: '#d4af37', color: '#000', border: 'none', fontWeight: 'bold', cursor: isSendingEmail ? 'not-allowed' : 'pointer', textTransform: 'uppercase' }}
                                >
                                    {isSendingEmail ? 'Sending Email...' : 'Send Shipping Email'}
                                </button>
                                <button
                                    type="button"
                                    onClick={() => setShowShippingModal(false)}
                                    style={{ flex: 1, padding: '12px', background: 'transparent', color: '#fff', border: '1px solid #333', cursor: 'pointer', textTransform: 'uppercase' }}
                                >
                                    Cancel
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            <div className="section-header">
                <h2>Order Management</h2>
                <p>Manage customer orders and track fulfillment</p>
            </div>

            <div className="stats-cards">
                <div className="stat-card">
                    <div className="stat-number">{statusCounts.all}</div>
                    <div className="stat-label">Total Orders</div>
                </div>
                <div className="stat-card">
                    <div className="stat-number">{statusCounts.paid}</div>
                    <div className="stat-label">New / Paid</div>
                </div>
                <div className="stat-card">
                    <div className="stat-number">{statusCounts.shipped}</div>
                    <div className="stat-label">Shipped</div>
                </div>
                <div className="stat-card">
                    <div className="stat-number">{statusCounts.delivered}</div>
                    <div className="stat-label">Delivered</div>
                </div>
            </div>

            <div className="filters-section">
                <div className="status-filters">
                    <button className={filter === 'all' ? 'active' : ''} onClick={() => setFilter('all')}>All Orders ({statusCounts.all})</button>
                    <button className={filter === 'paid' ? 'active' : ''} onClick={() => setFilter('paid')}>Paid ({statusCounts.paid})</button>
                    <button className={filter === 'shipped' ? 'active' : ''} onClick={() => setFilter('shipped')}>Shipped ({statusCounts.shipped})</button>
                    <button className={filter === 'delivered' ? 'active' : ''} onClick={() => setFilter('delivered')}>Delivered ({statusCounts.delivered})</button>
                </div>
            </div>

            <div className="orders-list">
                {displayedOrders.length === 0 ? (
                    <div className="empty-state">
                        <div className="empty-icon">📦</div>
                        <h3>No orders found</h3>
                        <p>There are no orders matching your current filter.</p>
                    </div>
                ) : (
                    displayedOrders.map(order => (
                        <div key={order.id} className={`order-card ${order.status}`}>
                            <div className="order-header">
                                <div className="order-info">
                                    <h3>Order #{formatOrderId(order.id)}</h3>
                                    <span className="order-date">
                                        {order.createdAt?.toDate().toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                                    </span>
                                </div>
                                <div className="order-status">
                                    <span className={`status-badge status-${order.status}`}>
                                        {order.status}
                                    </span>
                                    {(order.currency || '').toLowerCase() === 'usd' && (
                                        <span className="status-badge" style={{ background: 'rgba(96,165,250,0.15)', color: '#60a5fa', border: '1px solid rgba(96,165,250,0.4)' }}>
                                            US Order
                                        </span>
                                    )}
                                    <span className="order-total">{formatPrice(order.total, order.currency)}</span>
                                </div>
                            </div>

                            <div className="order-details">
                                <div className="customer-info">
                                    <h4>Customer</h4>
                                    <p><strong>{order.customerEmail}</strong></p>
                                </div>

                                <div className="shipping-info">
                                    <h4>Shipping Address</h4>
                                    {order.shippingAddress?.address ? (
                                        <>
                                            {order.shippingAddress.name && <p><strong>{order.shippingAddress.name}</strong></p>}
                                            <p>{order.shippingAddress.address}</p>
                                            {order.shippingAddress.address2 && <p>{order.shippingAddress.address2}</p>}
                                            <p>
                                                {order.shippingAddress.city}
                                                {order.shippingAddress.state ? `, ${order.shippingAddress.state}` : ''}
                                                {order.shippingAddress.zipCode ? `, ${order.shippingAddress.zipCode}` : ''}
                                            </p>
                                            <p>{order.shippingAddress.country}</p>
                                            {order.shippingAddress.phone && <p style={{ color: '#a0aec0', fontSize: '0.85rem' }}>📞 {order.shippingAddress.phone}</p>}
                                        </>
                                    ) : (
                                        <p style={{ color: '#888', fontStyle: 'italic' }}>Address securely captured via Apple/Google Pay</p>
                                    )}
                                    {/* Display tracking info if it exists! */}
                                    {order.trackingNumber && (
                                        <div style={{ marginTop: '10px', padding: '10px', background: 'rgba(212,175,55,0.05)', borderLeft: '2px solid #d4af37' }}>
                                            <p style={{ margin: 0, fontSize: '0.85rem' }}><span style={{ color: '#d4af37' }}>Courier:</span> {order.courier}</p>
                                            <p style={{ margin: 0, fontSize: '0.85rem' }}><span style={{ color: '#d4af37' }}>Tracking:</span> {order.trackingNumber}</p>
                                        </div>
                                    )}
                                </div>

                                <div className="order-items">
                                    <h4>Items ({order.items?.length || 0})</h4>
                                    <div className="items-list">
                                        {order.items?.map((item, index) => (
                                            <div key={index} className="order-item">
                                                <span className="item-name">{item.name}</span>
                                                <span className="item-quantity">Qty: {item.quantity}</span>
                                                <span className="item-price">{formatPrice(item.price, order.currency)}</span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>

                            <div className="order-actions">
                                <div className="status-actions">
                                    {order.status === 'paid' && (
                                        <>
                                            {/* --- OPEN MODAL INSTEAD OF INSTANT UPDATE --- */}
                                            <button
                                                className="btn btn-ship"
                                                onClick={() => { setOrderToShip(order); setShowShippingModal(true); }}
                                                style={{ backgroundColor: '#c5a059', color: '#000' }}
                                            >
                                                Mark as Shipped
                                            </button>
                                            <button className="btn btn-cancel" onClick={() => updateOrderStatus(order.ref, 'cancelled')}>
                                                Cancel
                                            </button>
                                        </>
                                    )}
                                    {order.status === 'shipped' && (
                                        <button
                                            className="btn btn-deliver"
                                            onClick={() => updateOrderStatus(order.ref, 'delivered')}
                                            style={{ backgroundColor: '#4CAF50', color: '#fff' }}
                                        >
                                            Mark as Delivered
                                        </button>
                                    )}
                                </div>
                                <button className="btn btn-secondary" onClick={() => setSelectedOrder(selectedOrder?.id === order.id ? null : order)}>
                                    {selectedOrder?.id === order.id ? 'Hide System Info' : 'View System Info'}
                                </button>
                            </div>

                            {selectedOrder?.id === order.id && (
                                <div className="order-expanded" style={{ borderTop: '1px solid rgba(212,175,55,0.2)', marginTop: '15px', paddingTop: '15px' }}>
                                    <div className="expanded-section">
                                        <h4>Stripe System Information</h4>
                                        <p style={{ fontFamily: 'monospace', fontSize: '0.8rem', color: '#888' }}>
                                            Stripe Session ID: {order.id}<br/>
                                            Currency: {order.currency?.toUpperCase()}<br/>
                                            Database Path: {order.ref.path}
                                        </p>
                                    </div>
                                </div>
                            )}
                        </div>
                    ))
                )}
            </div>
        </div>
    );
};

export default OrdersManager;